import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { BarChart3, LogOut, UserCheck } from "lucide-react";
import CombinedBallotForm from "../components/CombinedBallotForm";
import ResultsCharts from "../components/ResultsCharts";
import StudentAuth from "../components/StudentAuth";
import StudentProfileForm from "../components/StudentProfileForm";
import { Badge, Button, Card, SectionHeading, Spinner } from "../components/ui";
import { usePortal } from "../lib/portal";
import { api, type PollDto, type StudentDto } from "../lib/api";

// Voting page: students sign up / sign in with their identity (no ballot
// numbers), answer every open poll in ONE form, and submit once. The student
// sees their own ballots; the committee sees who voted for what (admin ledger);
// the public sees only aggregate results.

interface OwnBallot {
  pollId: number;
  pollTitle: string;
  category: string;
  changedAt: string;
  selected: Array<{ id: number; label: string }>;
}

export default function VotingPage() {
  const { data, reload } = usePortal();
  const { reload: reloadOutlet } = useOutletContext<{ reload: () => Promise<void> }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<StudentDto | null>(null);
  const [checking, setChecking] = useState(true);
  const [own, setOwn] = useState<OwnBallot[]>([]);

  const loadStudentState = useCallback(async () => {
    try {
      const me = await api.studentMe();
      setStudent(me.student);
      const { ballots } = await api.studentBallots();
      setOwn(ballots);
    } catch {
      setStudent(null);
      setOwn([]);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void loadStudentState();
  }, [loadStudentState]);

  const polls = data!.polls;
  const openPolls = polls.filter((p) => p.status === "open");
  const withResults = polls.filter((p) => p.resultsVisible);

  const refresh = () => {
    void reload();
    void reloadOutlet();
    void loadStudentState();
  };

  return (
    <div className="pt-[110px]">
      <section className="mx-auto max-w-shell px-5 pb-24 md:px-10">
        <SectionHeading
          eyebrow="[ VOTING CENTER ]"
          title="One form. One identity. Every poll."
          description="Sign in with your student details, answer every open poll in the single form below, and submit once — your ballots are recorded together. No ballot numbers: your registration is your identity."
          align="left"
        />
        <div className="mt-6 flex flex-wrap gap-2.5">
          <Badge tone="success">{openPolls.length} polls open now</Badge>
          <Badge tone="accent">1 student · 1 ballot per poll</Badge>
          <Badge tone="neutral">Revisions allowed until each deadline</Badge>
        </div>

        <div className="mt-10 space-y-6">
          {checking ? (
            <Spinner label="Checking your sign-in" />
          ) : student ? !student.profileComplete ? (
            <StudentProfileForm student={student} onSaved={(updated) => { setStudent(updated); void loadStudentState(); }} />
          ) : (
            <>
              <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div className="flex items-center gap-3">
                  <UserCheck className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
                  <div>
                    <p className="text-white">
                      Signed in as <strong>{student.fullName}</strong>
                    </p>
                    <p className="text-xs text-white/50">
                      Faculty {student.facultyNumber} · Enrollment {student.enrollmentNumber} · Semester{" "}
                      {student.semester}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void api.studentSignout().then(() => {
                      setStudent(null);
                      setOwn([]);
                    });
                  }}
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </Button>
              </Card>

              {openPolls.length === 0 ? (
                <Card className="p-8">
                  <p className="text-white/60">
                    No polls are open right now. The committee publishes each poll here — check back soon.
                  </p>
                </Card>
              ) : (
                <CombinedBallotForm polls={openPolls} onSubmitted={refresh} />
              )}

              {own.length > 0 && (
                <Card className="p-6">
                  <h3 className="text-lg font-medium text-white">Your ballots</h3>
                  <p className="mt-1 text-sm text-white/50">
                    Your choices are visible to the organizing committee (for verification) and to you
                    here. Public results stay aggregate only.
                  </p>
                  <ul className="mt-4 space-y-3">
                    {own.map((b) => (
                      <li key={b.pollId} className="rounded-lg border border-white/10 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-medium text-white">{b.pollTitle}</p>
                          <Badge tone="neutral">{b.category}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-white/70">
                          Your pick: {b.selected.map((s) => s.label).join(", ")}
                        </p>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </>
          ) : (
            <StudentAuth
              onSignedIn={(s) => {
                setStudent(s);
                void loadStudentState();
                navigate("/", { replace: true });
              }}
            />
          )}
        </div>

        {/* Combined results — every poll's aggregate together */}
        <div className="mt-16">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
            <h2 className="text-2xl font-semibold text-white">Combined results</h2>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-white/50">
            Aggregate results for all polls in one place. Individual names never appear here — the
            committee keeps the per-student ledger privately.
          </p>
          <div className="mt-6 space-y-6">
            {withResults.length === 0 ? (
              <Card className="p-8">
                <p className="text-white/60">Results will appear here as ballots come in.</p>
              </Card>
            ) : (
              withResults.map((poll: PollDto) => (
                <Card key={poll.id} className="p-6 md:p-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-[0.16em] text-wandor-prompt">
                        [ {poll.category} ]
                      </p>
                      <h3 className="mt-1 text-lg font-medium text-white">{poll.title}</h3>
                    </div>
                    <Badge tone="neutral">{poll.ballotsCast} ballots</Badge>
                  </div>
                  <div className="mt-4">
                    <ResultsCharts poll={poll} />
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
